import { DotsVertical } from '@untitledui/icons';
import type { FC, Key, ReactNode, Ref, RefObject } from 'react';
import { useCallback, useMemo } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import { cx } from '@/utils/cx';

export interface OverflowItem {
  /** Stable identity; also the key used by the overflow menu. */
  key: string;
  /** Inline rendering when the item is visible (e.g. a `role="tab"` button). */
  render: () => ReactNode;
  /** How the item appears inside the overflow (kebab) menu. */
  menu: {
    label: string;
    icon?: FC<{ className?: string }>;
    /** Marks the current item (e.g. the active tab is in overflow). */
    isActive?: boolean;
    onSelect: () => void;
  };
}

export interface OverflowBarProps {
  /** All items, in strict left-to-right order. */
  items: OverflowItem[];
  /** How many leading items render inline; the rest collapse into the menu. */
  visibleCount: number;
  /** Width-budget container ref from {@link useOverflow}. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Per-item ref setter from {@link useOverflow} (caches intrinsic widths). */
  setItemRef: (index: number) => (el: HTMLElement | null) => void;
  /** Highlights the kebab when an overflowed item is the active one. */
  triggerActive?: boolean;
  /** Ref to the kebab trigger button (e.g. to move focus to it). */
  triggerRef?: Ref<HTMLButtonElement>;
  /** ARIA role for the inline strip (e.g. `"tablist"`). */
  containerRole?: string;
  /** ARIA label for the inline strip. */
  ariaLabel?: string;
  /** Extra classes for the outer (measured) container. */
  className?: string;
  /**
   * Classes merged onto the overflow trigger button so it can be styled as an
   * exact peer of neighbouring icon buttons. Merged after the defaults; the
   * active-highlight wins over it so the kebab still reads as "current".
   */
  triggerClassName?: string;
  /** Classes merged onto the trigger icon (e.g. `size-3` for a delicate bar). */
  triggerIconClassName?: string;
}

/**
 * A horizontal strip that renders its leading items inline and collapses the
 * rest into a kebab (⋯) overflow menu, driven by {@link useOverflow}. Strict
 * left-to-right: items are never reordered; if the active item overflows it
 * stays in the menu and the kebab is highlighted (`triggerActive`).
 *
 * The OUTER container is the measured width budget; the inline items live in an
 * inner element that carries the consumer's ARIA role so the kebab (a menu
 * button) is a sibling of — not a child of — the role group (e.g. the tablist).
 */
export function OverflowBar({
  items,
  visibleCount,
  containerRef,
  setItemRef,
  triggerActive = false,
  triggerRef,
  containerRole,
  ariaLabel,
  className,
  triggerClassName,
  triggerIconClassName,
}: OverflowBarProps) {
  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      className={cx(
        'flex min-w-0 items-center gap-0.5 overflow-hidden',
        className
      )}
    >
      <div
        role={containerRole}
        aria-label={ariaLabel}
        className='flex min-w-0 items-center gap-0.5'
      >
        {visibleItems.map((item, index) => (
          <div key={item.key} ref={setItemRef(index)} className='flex shrink-0'>
            {item.render()}
          </div>
        ))}
      </div>
      {overflowItems.length > 0 && (
        <OverflowMenu
          items={overflowItems}
          active={triggerActive}
          triggerRef={triggerRef}
          triggerClassName={triggerClassName}
          triggerIconClassName={triggerIconClassName}
        />
      )}
    </div>
  );
}

interface OverflowMenuProps {
  items: OverflowItem[];
  active: boolean;
  triggerRef?: Ref<HTMLButtonElement>;
  triggerClassName?: string;
  triggerIconClassName?: string;
}

function OverflowMenu({
  items,
  active,
  triggerRef,
  triggerClassName,
  triggerIconClassName,
}: OverflowMenuProps) {
  const count = items.length;

  const handleAction = useCallback(
    (key: Key) => {
      items.find(item => item.key === key)?.menu.onSelect();
    },
    [items]
  );

  // The shared Dropdown.Menu runs in single-selection mode (its items expose
  // role="menuitemradio"), so we surface the current item via selectedKeys —
  // it renders a check + aria-checked, which conveys "this hidden item is
  // active" better than a plain menuitem could.
  const selectedKeys = useMemo(
    () => new Set(items.filter(item => item.menu.isActive).map(i => i.key)),
    [items]
  );

  return (
    <Dropdown.Root>
      {/* A raw react-aria Button (not Dropdown.DotsButton) because DotsButton
          hardcodes aria-label="Open menu", which would mask the contextual
          label below. `triggerClassName` lets the consumer make this an exact
          styling peer of neighbouring icon buttons; the active-highlight is
          applied last so it always wins. */}
      <AriaButton
        ref={triggerRef}
        aria-label={
          active
            ? `More tabs (${count}), current tab hidden`
            : `More tabs (${count})`
        }
        className={cx(
          'shrink-0 cursor-pointer rounded-lg p-1.5 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-[var(--color-bg-secondary)]',
          triggerClassName,
          active && 'bg-[var(--color-bg-secondary)] text-fg-primary'
        )}
      >
        <DotsVertical
          className={cx('size-5', triggerIconClassName)}
          aria-hidden='true'
        />
      </AriaButton>
      <Dropdown.Popover placement='bottom end'>
        <Dropdown.Menu
          selectedKeys={selectedKeys}
          onAction={handleAction}
          aria-label='More tabs'
        >
          {items.map(item => (
            <Dropdown.Item
              key={item.key}
              id={item.key}
              label={item.menu.label}
              icon={item.menu.icon}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
